import { BlurEvent, ChangeEvent, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, LookupModelSelectionEvent, Snackbar, Textbox } from "@mcleod/components";
import { LookupModelSearchEvent } from "@mcleod/components/src/events/LookupModelSearchEvent";
import { Api, StringUtil, UrlUtil } from "@mcleod/core";
import { AutogenLayoutScreenCode } from "./autogen/AutogenLayoutScreenCode";

export class ScreenCode extends AutogenLayoutScreenCode {

    override onLoad() {
        this.validateExistingRecord();
        this.textboxFieldCode.enabled = false;
        this.textboxFieldTableAlias.addChangeListener((event: ChangeEvent) => { this.updateCodeMaxLength()});
        this.textboxFieldName.addChangeListener((event: ChangeEvent) => { this.updateCodeMaxLength()});
    }

    sourceScreenCodesBeforeExecution(event: DataSourceExecutionEvent): void {
        if (event.getAction() === DataSourceAction.SEARCH) {
            this.applyUrlFiltersToSearch(event);
        }
    }

    applyUrlFiltersToSearch(event: DataSourceExecutionEvent) {
        const urlProps = UrlUtil.getPropsFromUrl(window.location.search);
        if (!urlProps) return;
        const { fieldCode, fieldName } = urlProps;
        if (fieldCode != null) {
            event.filter.field_code = fieldCode;
        }
        if (fieldName != null) {
            event.filter.field_name = fieldName;
        }
    }

    private validateExistingRecord() {
        this.sourceScreenCodes.doBeforeDataSourcePost = async () => {
            let result = this.sourceScreenCodes.validate();
            if (result && this.sourceScreenCodes.mode === DataSourceMode.ADD) {
                const fieldCode = this.sourceScreenCodes.activeRow?.get("field_code", null);
                const tableAlias = this.sourceScreenCodes.activeRow?.get("field_table_alias", null);
                const fieldName = this.sourceScreenCodes.activeRow?.get("field_name", null);
                try {
                    const response = await Api.post("lme/dispatch/check-duplicate-code", {
                        id: fieldCode,
                        table: "code",
                        alt_field_name_for_dup_check: "field_code",
                        composite_key_values: { field_table_alias: tableAlias, field_name: fieldName }
                    });

                    if (response.data?.[0]?.is_duplicate) {
                        const message = "Duplicate Code exists for: " + fieldCode + " with table: " + tableAlias + " and field: " + fieldName;
                        Snackbar.showWarningSnackbar(message);
                        result = false;
                    }
                } catch (error) {
                    Snackbar.showWarningSnackbar("Failed to check for duplicate code.");
                    result = false;
                }
            }
            return result;
        };
    }

    async updateCodeMaxLength() {
        const tableAlias = this.textboxFieldTableAlias.text;
        const fieldName = this.textboxFieldName.text;
        let maxLength = 8;

        if (StringUtil.isEmptyString(tableAlias) || StringUtil.isEmptyString(fieldName)) {
            this.textboxFieldCode.placeholder = `Max length ${maxLength} Characters`;
            this.textboxFieldCode.enabled = false;
            return;
        }

        this.textboxFieldCode.enabled = true;

        const inputs = { table_alias: tableAlias, field_name: fieldName };
        const response = await Api.search("lme/dispatch/validate-screen-code-field", inputs);

        if (response) {
            const data = response.data?.[0];
            if (data?.success === true) {
                const maximumLength = data?.max_length;
                if (maximumLength !== undefined) {
                    maxLength = maximumLength > 8 ? maxLength : maximumLength;
                }
                this.textboxFieldCode.placeholder = `Max length ${maxLength} Characters`;
            }
        }
    }

    /**
     * Sets the filter for table_name based on the table alias input before performing a lookup.
     * If the alias is empty, the lookup is prevented.
     */
    textboxFieldNameBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        if (!StringUtil.isEmptyString(this.textboxFieldTableAlias.text))
            event.filter.table_name = this.textboxFieldTableAlias.text;
        else {
            event.preventDefault();
        }
    }

    async textboxFieldCodeOnBlur(event: BlurEvent) {
        const code = event.target as Textbox;
        if (StringUtil.isEmptyString(code.text)) {
            Snackbar.showWarningSnackbar("Code is required");
            return;
        }
        const tableAlias = this.textboxFieldTableAlias.text;
        const fieldName = this.textboxFieldName.text;
        const inputs = { "table_alias": tableAlias, "field_name": fieldName };
        const response = await Api.search("lme/dispatch/validate-screen-code-field", inputs)
        if (response) {
            const data = response.data?.[0];
            if (data?.success === false) {
                Snackbar.showWarningSnackbar(data?.message);
                code.text = "";
            }
            const maxLength = data?.max_length;
            if (maxLength && code.text.length > maxLength) {
                Snackbar.showWarningSnackbar(`Code must not exceed ${maxLength} characters`);
                code.text = "";
                return;
            }
        }
    }

    textboxFieldTableAliasOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection() && !StringUtil.isEmptyString(event.selection.get("name"))){
            this.sourceScreenCodes.activeRow.set("field_name", null);
            this.sourceScreenCodes.activeRow.set("field_code", null);
        }
    }

    textboxFieldNameOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection() && !StringUtil.isEmptyString(event.selection.get("field_name"))) {
            this.sourceScreenCodes.activeRow.set("field_code", null);
        }
    }
}
